// Takip (v1.22.0) — ortak tipler, kayıt türleri ve olay yardımcıları.

export type IlgiliTur = 'CARI' | 'LEAD' | 'SOZLESME' | 'SUNUM' | 'PROJE' | 'EMLAK' | 'KIRALIK' | 'KATKARSILIK' | 'RANDEVU';

export interface TakipOzet {
    todoID: number;
    kullaniciID: number;
    kullaniciAdi?: string | null;
    olusturanKullaniciID?: number | null;
    olusturanAdi?: string | null;
    baslik: string;
    aciklama?: string | null;
    renk: string;
    tamamlandi: boolean;
    tamamlanmaTarihi?: string | null;
    siraNo: number;
    olusturmaTarihi: string;
    guncellemeTarihi?: string | null;
    gorevTarihi?: string | null;
    baslangicSaati?: string | null;
    bitisSaati?: string | null;
    ilgiliTur?: IlgiliTur | null;
    ilgiliAnahtar?: string | null;
    ilgiliBaslik?: string | null;
    ustTakipID?: number | null;
    ustTakipBaslik?: string | null;
    talepTuru?: 'BILGI' | 'SUREC' | null;
    notSayisi: number;
    dosyaSayisi: number;
    altTakipSayisi: number;
    acikAltTakipSayisi: number;
}

export interface TakipNotu { notID: number; kullaniciID: number; kullaniciAdi: string; metin: string; tarih: string; }
export interface TakipDosyasi { dosyaID: number; orijinalAd: string; boyut: number; yukleyenID: number; yukleyenAdi: string; tarih: string; }

export interface TakipDetay {
    takip: TakipOzet;
    notlar: TakipNotu[];
    dosyalar: TakipDosyasi[];
    altTakipler: TakipOzet[];
    ustTakip: { todoID: number; baslik: string; tamamlandi: boolean; kullaniciAdi: string } | null;
    duzenleyebilir: boolean;
    silebilir: boolean;
}

export interface TakipKullanici { kullaniciID: number; adSoyad: string; }

// Takibin bağlanacağı kayıt (sayfalardan verilir)
export interface IlgiliKayit { tur: IlgiliTur; anahtar: string | number; baslik: string; }

export const ILGILI_TURLER: { tur: IlgiliTur; ad: string; ikon: string; renk: string; url: (a: string) => string }[] = [
    { tur: 'CARI',        ad: 'Müşteri',              ikon: 'fa-user-tie',     renk: '#2563eb', url: a => `/musteriler/${a}` },
    { tur: 'LEAD',        ad: 'Potansiyel Müşteri',   ikon: 'fa-user-plus',    renk: '#0891b2', url: a => `/lead-takip?lead=${a}` },
    { tur: 'SOZLESME',    ad: 'Sözleşme',             ikon: 'fa-file-signature', renk: '#7c3aed', url: a => `/sozlesme-detay/${a}` },
    { tur: 'SUNUM',       ad: 'Sunum - Teklif',       ikon: 'fa-file-invoice', renk: '#db2777', url: a => `/musteri-sunumlari/${a}` },
    { tur: 'PROJE',       ad: 'Proje',                ikon: 'fa-building',     renk: '#ea580c', url: a => `/projeler/${a}` },
    { tur: 'EMLAK',       ad: 'Emlak',                ikon: 'fa-home',         renk: '#16a34a', url: a => `/emlak-portfoy/${a}` },
    { tur: 'KIRALIK',     ad: 'Kiralık Gayrimenkul',  ikon: 'fa-key',          renk: '#0d9488', url: a => `/kiralik/portfoy?id=${a}` },
    { tur: 'KATKARSILIK', ad: 'Kat Karşılığı Arsa',   ikon: 'fa-layer-group',  renk: '#a16207', url: a => `/kat-karsiligi/${a}` },
    { tur: 'RANDEVU',     ad: 'Randevu',              ikon: 'fa-calendar-check', renk: '#0d9488', url: a => `/randevular?randevu=${a}` },
];
export const ilgiliMeta = (tur?: string | null) => ILGILI_TURLER.find(x => x.tur === tur);

export const TAKIP_RENKLERI = ['#fde8e8', '#fef3c7', '#d1fae5', '#dbeafe', '#ede9fe', '#f8e4d0'];

// Takip bir kişisel görev değil mi? (atanmış, kayda bağlı ya da alt takip)
export const takipMi = (t: { olusturanKullaniciID?: number | null; ilgiliTur?: string | null; ustTakipID?: number | null; aciklama?: string | null }) =>
    t.olusturanKullaniciID != null || !!t.ilgiliTur || t.ustTakipID != null;

export function bugunStr(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function tarihEtiketi(t: string | null | undefined): string {
    if (!t) return '';
    const d = new Date(t.length === 10 ? t + 'T00:00:00' : t);
    return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined });
}

export function zamanEtiketi(t: string): string {
    const d = new Date(t);
    return d.toLocaleString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function boyutEtiketi(b: number): string {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export type TarihDurumu = 'geciken' | 'bugun' | 'yakin' | 'ileri' | null;
export function tarihDurumu(t: TakipOzet): TarihDurumu {
    if (!t.gorevTarihi || t.tamamlandi) return null;
    const bugun = bugunStr();
    if (t.gorevTarihi < bugun) return 'geciken';
    if (t.gorevTarihi === bugun) return 'bugun';
    const fark = (new Date(t.gorevTarihi + 'T00:00:00').getTime() - new Date(bugun + 'T00:00:00').getTime()) / 86400000;
    return fark <= 7 ? 'yakin' : 'ileri';
}

export function hataMesaji(e: any, varsayilan = 'İşlem sırasında hata oluştu.'): string {
    return e?.response?.data?.message || varsayilan;
}

// ── Olaylar: herhangi bir yerden takip detayını aç / listeleri yenile ──
export const TAKIP_AC_OLAYI = 'yapicrm:takip-ac';
export const TAKIP_DEGISTI_OLAYI = 'yapicrm:takip-degisti';
export const takipAc = (id: number) => window.dispatchEvent(new CustomEvent(TAKIP_AC_OLAYI, { detail: id }));
export const takipDegisti = () => window.dispatchEvent(new CustomEvent(TAKIP_DEGISTI_OLAYI));
